/**
 * KatalogFilterPanelContent — thin wrapper around canonical FeedFilterPanelContent.
 *
 * Supplies the Katalog "Feed Focus" selector via the `extrasAbovePills` slot,
 * so the shared component stays small and unbiased.
 */
import * as React from 'react';
import { cn } from '../../lib/utils';
import {
  Check,
  CheckCircle,
  Pause,
  Archive,
  BookOpen,
  Calendar,
  Briefcase,
} from 'lucide-react';
import {
  FeedFilterPanelContent,
  FeedFilterSection,
  type FeedSectionOption,
  type FeedTaxonomyOption,
} from '@bright-web-skeleton/ui';
import type { KatalogSectionKey } from './KatalogShortcutRail';

export type KatalogFocus = 'katalog' | 'events' | 'portfolio';

export type TaxonomyOption = FeedTaxonomyOption;

export interface KatalogFilterPanelContentProps {
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  activeSections: KatalogSectionKey[];
  onToggleSection: (section: KatalogSectionKey) => void;
  /** Reserved (currently unused in UI) */
  dealType: string;
  onDealTypeChange: (type: string) => void;
  focus: KatalogFocus;
  onFocusChange: (focus: KatalogFocus) => void;
  industryOptions?: TaxonomyOption[];
  activeIndustryIds?: string[];
  onToggleIndustry?: (id: string) => void;
  categoryOptions?: TaxonomyOption[];
  activeCategoryIds?: string[];
  onToggleCategory?: (id: string) => void;
  onApply: () => void;
  onClear: () => void;
  onClearAll: () => void;
  hasCommittedFilters?: boolean;
  draftMatchesCommitted?: boolean;
}

const SECTION_OPTIONS: FeedSectionOption<KatalogSectionKey>[] = [
  { key: 'active', label: 'Active', icon: CheckCircle },
  { key: 'paused', label: 'Paused', icon: Pause },
  { key: 'ended', label: 'Ended', icon: Archive },
];

const FOCUS_OPTIONS: { key: KatalogFocus; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'katalog', label: 'Katalag', icon: BookOpen },
  { key: 'events', label: 'Events', icon: Calendar },
  { key: 'portfolio', label: 'Portfolio', icon: Briefcase },
];

export const KatalogFilterPanelContent: React.FC<KatalogFilterPanelContentProps> = ({
  focus,
  onFocusChange,
  dealType: _dealType,
  onDealTypeChange: _onDealTypeChange,
  ...rest
}) => {
  const focusExtras = (
    <FeedFilterSection title="Feed Focus">
      <div className="flex gap-2">
        {FOCUS_OPTIONS.map((option) => {
          const isActive = focus === option.key;
          const Icon = option.icon;
          return (
            <button
              key={option.key}
              onClick={() => onFocusChange(option.key)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors select-none',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-foreground border border-border hover:bg-muted',
              )}
            >
              {isActive && <Check className="w-3 h-3 shrink-0" />}
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    </FeedFilterSection>
  );

  return (
    <FeedFilterPanelContent<KatalogSectionKey>
      {...rest}
      sectionOptions={SECTION_OPTIONS}
      sectionTitle="Campaign Status"
      searchPlaceholder="Search your katalog..."
      extrasAbovePills={focusExtras}
      extrasHasChanges={focus !== 'katalog'}
    />
  );
};

export default KatalogFilterPanelContent;